import {
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { getBuildingDescriptionLabel } from "web_myplann/Portfolio/Forms/BuildingShared/buildingDescriptionLabel";
import type { BuildingRealEstateType } from "web_myplann/Portfolio/Forms/RealEstateForm/realEstateTypes";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import {
	optionalDateAsIsoString,
	optionalPositiveInteger,
	optionalZipCode,
	trimmedNullable,
} from "web_myplann/Shared/Validation/zodHelpers";
import { z } from "zod";

const MAX_FLOORS = 999;

export const getBuildingFormObjectSchema = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddRealEstate.Validations.maxValue);
	const positiveIntegerMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddRealEstate.Validations.positiveInteger,
		),
		maxMessage,
	};
	const maxLength = (max: number) =>
		t(Resources.Portfolio.AddRealEstate.Validations.maxLength, { max });

	return z.object({
		location: trimmedNullable(z.string().max(80, maxLength(80))),
		locationMunicipality: trimmedNullable(z.string().max(50, maxLength(50))),
		locationZipCode: optionalZipCode(
			t(Resources.Portfolio.AddRealEstate.Validations.zipFormat),
		),
		usableArea: optionalPositiveInteger(
			ValidationLimits.Amount.Max,
			positiveIntegerMessages,
		),
		floorArea: optionalPositiveInteger(
			ValidationLimits.Amount.Max,
			positiveIntegerMessages,
		),
		disposition: z.nativeEnum(ClientPropertyBuildingDispositionEnum).nullable(),
		buildingMaterial: z
			.nativeEnum(ClientPropertyBuildingMaterialEnum)
			.nullable(),
		numberOfFloors: optionalPositiveInteger(
			MAX_FLOORS,
			positiveIntegerMessages,
		),
		interiorValue: optionalPositiveInteger(
			ValidationLimits.Amount.Max,
			positiveIntegerMessages,
		),
		purchaseDate: optionalDateAsIsoString(),
		description: trimmedNullable(z.string().max(3000, maxLength(3000))),
		terrace: z.boolean(),
		loggia: z.boolean(),
		balcony: z.boolean(),
		garage: z.boolean(),
		cellar: z.boolean(),
		elevator: z.boolean(),
		barrierFree: z.boolean(),
		swimmingPool: z.boolean(),
		forInvestments: z.boolean(),
	});
};

type BuildingRequiredMinimumFields = {
	location: unknown;
	locationMunicipality: unknown;
	description: unknown;
};

export const withBuildingRequiredMinimum = <T extends z.AnyZodObject>(
	schema: T,
	t: TranslateFunction,
	type: BuildingRealEstateType,
) => {
	const requiredMinimumMessage = t(
		Resources.Portfolio.AddRealEstate.Validations.requiredMinimum,
		{
			fields: [
				t(Resources.PortfolioDetail.Property.StreetAndNumber),
				t(Resources.PortfolioDetail.Property.Municipality),
				getBuildingDescriptionLabel(t, type),
			].join(", "),
		},
	);

	return schema.superRefine((rawData, ctx) => {
		// Every caller passes an object schema that carries these three fields
		// (they are part of the shared building shape and never omitted).
		const data = rawData as BuildingRequiredMinimumFields;
		const hasMinimum =
			data.location !== null ||
			data.locationMunicipality !== null ||
			data.description !== null;

		if (!hasMinimum) {
			for (const path of [
				"location",
				"locationMunicipality",
				"description",
			] as const) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [path],
					message: requiredMinimumMessage,
				});
			}
		}
	});
};

export const getBuildingFormSchema = (
	t: TranslateFunction,
	type: BuildingRealEstateType,
) => withBuildingRequiredMinimum(getBuildingFormObjectSchema(t), t, type);

export type BuildingFormInput = z.input<
	ReturnType<typeof getBuildingFormSchema>
>;
export type BuildingFormOutput = z.output<
	ReturnType<typeof getBuildingFormSchema>
>;
